import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useCompany } from '@/context/CompanyContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { toast } from '@/hooks/use-toast';

export interface Product {
  id: string;
  company_id: string;
  name: string;
  price: number;
  quantity: number;
  min_threshold: number;
  barcode?: string;
  image_url?: string;
  cycle_de_vie?: 'best_seller' | 'en_promotion' | 'fin_de_serie' | 'archive';
  created_at: string;
  updated_at: string;
}

interface InventoryContextType {
  products: Product[];
  visibleProducts: Product[];
  hiddenProducts: Product[];
  loading: boolean;
  addProduct: (product: Omit<Product, 'id' | 'company_id' | 'created_at' | 'updated_at'>) => Promise<boolean>;
  updateProduct: (id: string, updates: Partial<Omit<Product, 'id' | 'company_id' | 'created_at' | 'updated_at'>>) => Promise<boolean>;
  deleteProduct: (id: string) => Promise<boolean>;
  getLowStockProducts: () => Product[];
  refreshProducts: () => Promise<void>;
}

const SupabaseInventoryContext = createContext<InventoryContextType | undefined>(undefined);

export const SupabaseInventoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { currentCompany, userRole } = useCompany();
  const { hasAccess } = useSubscription();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);

  // Calculer les produits visibles et masqués - limite à 10 lignes pour les tableaux
  const visibleProducts = hasAccess ? products : products.slice(0, 10);
  const hiddenProducts = hasAccess ? [] : products.slice(10);

  useEffect(() => {
    if (currentCompany && user) {
      fetchProducts();
    } else {
      setProducts([]);
    }
  }, [currentCompany, user]);

  const fetchProducts = async () => {
    if (!currentCompany) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('company_id', currentCompany.id)
        .order('name');

      if (error) throw error;

      // Filtrer l'état "nouveau" qui a été retiré
      const filteredData = (data || []).map(product => ({
        ...product,
        cycle_de_vie: product.cycle_de_vie === 'nouveau' ? undefined : product.cycle_de_vie
      }));

      setProducts(filteredData as Product[]);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les produits",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addProduct = async (productData: Omit<Product, 'id' | 'company_id' | 'created_at' | 'updated_at'>): Promise<boolean> => {
    if (!currentCompany) {
      toast({
        title: "Erreur",
        description: "Aucune entreprise sélectionnée",
        variant: "destructive",
      });
      return false;
    }

    if (userRole !== 'admin') {
      toast({
        title: "Accès refusé",
        description: "Seuls les administrateurs peuvent ajouter des produits",
        variant: "destructive",
      });
      return false;
    }

    // Vérifier la limite de 10 produits pour les utilisateurs non-premium
    if (!hasAccess && products.length >= 10) {
      toast({
        title: "Limite atteinte",
        description: "Vous pouvez créer jusqu'à 10 produits en mode démo. Passez à Premium pour un accès illimité.",
        variant: "destructive",
      });
      return false;
    }

    try {
      // Prevent duplicate barcode within the same company
      if (productData.barcode && productData.barcode.trim() !== '') {
        const { data: dupRows, error: dupError } = await supabase
          .from('products')
          .select('id')
          .eq('company_id', currentCompany.id)
          .eq('barcode', productData.barcode);

        if (dupError) throw dupError;

        if (dupRows && dupRows.length > 0) {
          toast({
            title: "Code-barres déjà utilisé",
            description: `Un produit avec le code-barres "${productData.barcode}" existe déjà`,
            variant: "destructive",
          });
          return false;
        }
      }

      const { data, error } = await supabase
        .from('products')
        .insert([{
          ...productData,
          company_id: currentCompany.id,
        }])
        .select()
        .single();

      if (error) throw error;

      // Log activity
      await supabase.rpc('log_activity', {
        p_action: 'CREATE',
        p_entity_type: 'product',
        p_entity_name: productData.name,
        p_details: { product_id: data.id }
      });

      await fetchProducts();
      
      toast({
        title: "Succès",
        description: `Produit "${productData.name}" ajouté avec succès`,
      });

      return true;
    } catch (error) {
      console.error('Error adding product:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter le produit",
        variant: "destructive",
      });
      return false;
    }
  };

  const updateProduct = async (id: string, updates: Partial<Omit<Product, 'id' | 'company_id' | 'created_at' | 'updated_at'>>): Promise<boolean> => {
    if (userRole !== 'admin') {
      toast({
        title: "Accès refusé",
        description: "Seuls les administrateurs peuvent modifier des produits",
        variant: "destructive",
      });
      return false;
    }

    try {
      const product = products.find(p => p.id === id);
      if (!product) throw new Error('Product not found');

      // Capturer les anciennes valeurs avant la mise à jour
      const oldValues: any = {};
      const newValues: any = {};
      
      Object.keys(updates).forEach(key => {
        oldValues[key] = product[key as keyof typeof product];
        newValues[key] = updates[key];
      });

      const { error } = await supabase
        .from('products')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      // Log activity with old and new values
      await supabase.rpc('log_activity', {
        p_action: 'UPDATE',
        p_entity_type: 'product',
        p_entity_name: product.name,
        p_details: { 
          product_id: id, 
          updates: newValues,
          old_values: oldValues,
          changes: Object.keys(updates).reduce((acc, key) => {
            acc[key] = {
              from: oldValues[key],
              to: newValues[key]
            };
            return acc;
          }, {} as any)
        }
      });

      await fetchProducts();
      
      toast({
        title: "Succès",
        description: "Produit modifié avec succès",
      });

      return true;
    } catch (error) {
      console.error('Error updating product:', error);
      toast({
        title: "Erreur",
        description: "Impossible de modifier le produit",
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteProduct = async (id: string): Promise<boolean> => {
    if (userRole !== 'admin') {
      toast({
        title: "Accès refusé",
        description: "Seuls les administrateurs peuvent supprimer des produits",
        variant: "destructive",
      });
      return false;
    }

    try {
      const product = products.find(p => p.id === id);
      if (!product) throw new Error('Product not found');

      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Log activity
      await supabase.rpc('log_activity', {
        p_action: 'DELETE',
        p_entity_type: 'product',
        p_entity_name: product.name,
        p_details: { product_id: id }
      });

      await fetchProducts();
      
      toast({
        title: "Succès",
        description: "Produit supprimé avec succès",
      });

      return true;
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le produit",
        variant: "destructive",
      });
      return false;
    }
  };

  const getLowStockProducts = (): Product[] => {
    return products.filter(product => product.quantity < product.min_threshold);
  };

  const refreshProducts = async () => {
    await fetchProducts();
  };

  return (
    <SupabaseInventoryContext.Provider value={{
      products,
      visibleProducts,
      hiddenProducts,
      loading,
      addProduct,
      updateProduct,
      deleteProduct,
      getLowStockProducts,
      refreshProducts,
    }}>
      {children}
    </SupabaseInventoryContext.Provider>
  );
};

export const useSupabaseInventory = () => {
  const context = useContext(SupabaseInventoryContext);
  if (context === undefined) {
    throw new Error('useSupabaseInventory must be used within a SupabaseInventoryProvider');
  }
  return context;
};